/**
 * OrderActivity.tsx — who did what to an order, and when (placed, re-sent,
 * cancelled, retried, accepted by the pharmacy). Read from the order's
 * activity log (integration_order_events); orders placed before the log
 * existed simply show nothing.
 */
import { useQuery } from '@tanstack/react-query';
import { getIntegrationOrderEvents, type IntegrationOrderEvent } from '@/services/integrations.service';

function describe(e: IntegrationOrderEvent): string {
  const d = e.details ?? {};
  switch (e.action) {
    case 'created':
      return 'Sent the order';
    case 'resent':
      return d.resentFromOrderId ? `Re-sent cancelled order #${d.resentFromOrderId} as this order` : 'Re-sent a cancelled order';
    case 'resent_as':
      return d.newOrderId ? `Re-sent this order as order #${d.newOrderId}` : 'Re-sent this order';
    case 'cancelled':
      return d.sentToPharmacy ? 'Cancelled the order with Axiom' : 'Cancelled the order (before it reached the pharmacy)';
    case 'retried':
      return d.accepted ? 'Retried — the pharmacy accepted it' : `Retried — the pharmacy rejected it again${d.error ? `: ${d.error}` : ''}`;
    case 'submitted':
      return 'The pharmacy accepted the order';
    // Clinic handoff (SCRUM-2200).
    case 'handoff.reconstituted':
      return 'Marked the order reconstituted';
    case 'handoff.reconstituted_undone':
      return 'Undid "reconstituted"';
    case 'handoff.picked_up':
      return 'Marked the order picked up by the patient';
    case 'handoff.picked_up_undone':
      return 'Undid "picked up"';
    default:
      return e.action;
  }
}

const when = (at: string) =>
  new Date(at.replace(' ', 'T')).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

export function OrderActivity({ patientId, orderId }: { patientId: number; orderId: number }) {
  const { data: events = [], isLoading } = useQuery({
    queryKey: ['integration-order-events', patientId, orderId],
    queryFn: () => getIntegrationOrderEvents(patientId, orderId),
    staleTime: 30_000,
  });

  if (isLoading) return <p className="text-sm text-slate-400">Loading activity…</p>;
  if (events.length === 0) return <p className="text-sm text-slate-400">No activity recorded for this order.</p>;

  return (
    <ul className="space-y-1.5">
      {events.map((e, i) => (
        <li key={`${e.at}-${i}`} className="flex flex-col gap-0.5 text-sm sm:flex-row sm:items-baseline sm:justify-between sm:gap-3">
          <span className="text-slate-700">
            <b className="font-medium text-slate-900">{e.userName ?? (e.userId ? `User #${e.userId}` : 'System')}</b>
            {' — '}
            {describe(e)}
          </span>
          <span className="shrink-0 text-xs text-slate-500">{when(e.at)}</span>
        </li>
      ))}
    </ul>
  );
}
